import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button, BrandMark } from "../components/ui-kit";
import AppDownloadButtons from "../components/AppDownloadButtons";
import { Lightning, MapTrifold, ShieldCheck, Wallet, Star, ArrowRight, Percent, Coins, Crown, Buildings, CreditCard, Storefront, ShoppingCart } from "@phosphor-icons/react";

const FEATURES = [
  { icon: Storefront, title: "Restaurants", text: "Menus, panier client, preuve Mobile Money et livraison par chauffeur." },
  { icon: ShoppingCart, title: "Supermarches", text: "Courses en ligne, produits partenaires et livraison a domicile." },
  { icon: Lightning, title: "Dispatch instantané", text: "Le chauffeur certifié le plus proche accepte votre course en quelques secondes." },
  { icon: MapTrifold, title: "10 villes RDC", text: "Bunia, Kinshasa, Lubumbashi, Goma, Kisangani... un seul app partout." },
  { icon: Wallet, title: "Paiement direct", text: "Cash, M-Pesa, Airtel Money et Orange Money avec confirmation chauffeur." },
  { icon: ShieldCheck, title: "Code PIN de sécurité", text: "Chaque course a un PIN que seul le vrai chauffeur peut saisir." },
  { icon: Star, title: "Fidélité & parrainage", text: "Bronze → Platine, 2 000 CDF bonus à chaque filleul." },
];

const REVENUE = [
  { icon: Percent, title: "Commission course", text: "La plateforme garde une commission sur chaque course terminee." },
  { icon: Coins, title: "Frais de service", text: "Un petit frais transparent peut etre ajoute au prix client." },
  { icon: Crown, title: "Abonnements chauffeurs", text: "Forfaits journaliers ou mensuels avec moins de commission." },
  { icon: CreditCard, title: "Paiements sponsorises", text: "Revenus via campagnes annonceurs, promos payantes et confirmations Mobile Money." },
  { icon: Buildings, title: "Contrats entreprises", text: "Offres pour hotels, ONG, societes, aeroport et livraisons reservees." },
];

export default function Landing() {
  return (
    <div className="relative app-screen bg-premium-grad text-foreground">
      {/* Top bar */}
      <header className="public-header-safe mx-auto flex max-w-6xl items-center justify-between gap-2 px-5">
        <BrandMark className="public-header-brand" />
        <div className="public-header-actions flex items-center justify-end gap-2">
          <Link to="/login" data-testid="nav-login">
            <Button variant="ghost" className="text-xs">Connexion</Button>
          </Link>
          <Link to="/register" data-testid="nav-register">
            <Button variant="primary" className="text-xs">Commencer</Button>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="mx-auto grid max-w-6xl grid-cols-1 gap-10 px-5 pb-16 pt-7 lg:grid-cols-2 lg:pt-16">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest text-primary">
            <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
            Tarif automatique · Zéro négociation
          </div>
          <h1 className="landing-hero-title font-heading font-bold leading-[0.95] tracking-tight sm:text-6xl lg:text-7xl">
            Bouger en{" "}
            <span className="text-gradient-premium">RDC</span>.
            <br />
            Sans friction.
          </h1>
          <p className="mt-6 max-w-md text-base text-muted-foreground">
            La super-app mobilité congolaise. Moto, economy, comfort, premium ou XL —
            prix calculé en direct, chauffeur géolocalisé, paiement Mobile Money.
          </p>
          <div className="mt-8 flex flex-col gap-3 min-[390px]:flex-row min-[390px]:flex-wrap">
            <Link to="/register" data-testid="hero-cta-register">
              <Button variant="primary" className="w-full px-6 py-4 text-base min-[390px]:w-auto">
                Commander ma course <ArrowRight weight="bold" />
              </Button>
            </Link>
            <Link to="/register" data-testid="hero-cta-driver">
              <Button variant="outline" className="w-full px-6 py-4 text-base min-[390px]:w-auto">
                Devenir chauffeur
              </Button>
            </Link>
          </div>
          <AppDownloadButtons className="mt-4 max-w-md" />
          <div className="mt-4 grid max-w-md grid-cols-2 gap-3">
            <Link to="/register" data-testid="hero-cta-food" className="rounded-2xl border border-primary/25 bg-primary/10 p-4 press-down hover:bg-primary/15">
              <Storefront size={22} weight="duotone" className="text-primary" />
              <div className="mt-2 font-heading text-base font-black">Restaurants</div>
              <div className="mt-1 text-xs text-muted-foreground">Repas livres par Nexora.</div>
            </Link>
            <Link to="/register" data-testid="hero-cta-market" className="rounded-2xl border border-secondary/25 bg-secondary/10 p-4 press-down hover:bg-secondary/15">
              <ShoppingCart size={22} weight="duotone" className="text-secondary" />
              <div className="mt-2 font-heading text-base font-black">Supermarches</div>
              <div className="mt-1 text-xs text-muted-foreground">Courses a domicile.</div>
            </Link>
          </div>
          <div className="mt-10 grid grid-cols-3 gap-3">
            {[
              { k: "Villes", v: "10" },
              { k: "Véhicules", v: "5 types" },
              { k: "Moto dès", v: "1 000 CDF" },
            ].map((s) => (
              <div key={s.k} className="rounded-2xl glass p-3">
                <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{s.k}</div>
                <div className="font-heading text-xl font-bold">{s.v}</div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Phone mockup */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="flex items-center justify-center"
        >
          <div className="relative w-[320px] rounded-[3rem] border border-white/15 bg-card p-3 glow-blue">
            <div className="h-[600px] overflow-hidden rounded-[2.5rem] bg-background">
              <div className="relative h-full">
                <div
                  className="absolute inset-0"
                  style={{
                    background:
                      "radial-gradient(400px 200px at 50% 0%, hsla(190,100%,50%,0.25), transparent 70%)",
                  }}
                />
                <div className="relative p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Bonjour</div>
                      <div className="font-heading text-lg font-bold">Amani</div>
                    </div>
                    <div className="h-8 w-8 rounded-full bg-gradient-to-br from-primary to-secondary" />
                  </div>
                  <div className="mt-5 rounded-2xl glass p-4">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-primary">Estimation</div>
                    <div className="font-heading text-2xl font-bold">Centre-Ville → Mudzipela</div>
                    <div className="mt-3 grid grid-cols-2 gap-2">
                      <div className="rounded-xl border border-white/10 p-2">
                        <div className="text-[10px] text-muted-foreground">Moto</div>
                        <div className="font-mono font-bold">1 800 CDF</div>
                      </div>
                      <div className="rounded-xl border border-primary/30 bg-primary/10 p-2">
                        <div className="text-[10px] text-primary">Economy</div>
                        <div className="font-mono font-bold text-primary">4 500 CDF</div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 flex items-center justify-between rounded-2xl glass p-3">
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Points</div>
                      <div className="font-heading text-xl font-bold text-secondary">1 240 pts</div>
                    </div>
                    <div className="rounded-full bg-secondary/20 px-3 py-1 text-xs font-bold text-secondary">
                      ★ ARGENT
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="absolute left-1/2 top-0 h-6 w-28 -translate-x-1/2 rounded-b-2xl bg-black" />
          </div>
        </motion.div>
      </section>

      {/* Features grid */}
      <section className="mx-auto max-w-6xl px-5 pb-24">
        <h2 className="font-heading text-3xl font-bold sm:text-4xl">
          Une vraie super-app. <span className="text-muted-foreground">Construite pour la RDC.</span>
        </h2>
        <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05 }}
              className="rounded-2xl glass p-5"
            >
              <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <f.icon size={22} weight="duotone" />
              </div>
              <div className="font-heading text-lg font-bold">{f.title}</div>
              <div className="mt-1 text-sm text-muted-foreground">{f.text}</div>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-5 pb-24">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-widest text-secondary">
              Modele economique
            </div>
            <h2 className="mt-2 font-heading text-3xl font-bold sm:text-4xl">
              Une plateforme rentable. <span className="text-muted-foreground">Sans casser le prix client.</span>
            </h2>
          </div>
          <Link to="/terms" className="text-xs font-bold uppercase tracking-widest text-primary">
            Voir la politique
          </Link>
        </div>
        <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
          {REVENUE.map((item) => (
            <div key={item.title} className="rounded-2xl glass p-4">
              <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-secondary/10 text-secondary">
                <item.icon size={22} weight="duotone" />
              </div>
              <div className="font-heading text-base font-bold">{item.title}</div>
              <div className="mt-1 text-xs leading-5 text-muted-foreground">{item.text}</div>
            </div>
          ))}
        </div>
      </section>

      <footer className="border-t border-white/5 py-8 text-center text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">
        <div>NEXORA-CAB - 10 villes RDC - Mobilite urbaine premium</div>
        <div className="mt-3 flex flex-wrap justify-center gap-4 tracking-widest">
          <Link to="/privacy" className="hover:text-primary">Confidentialite</Link>
          <Link to="/terms" className="hover:text-primary">Politique</Link>
        </div>
      </footer>
    </div>
  );
}
